import { get } from 'lodash';

import { REGEX, STRINGS } from '../constants';
import { addTrailingZeros } from '../helpers/normalizers';

export const str = (values, path) => get(values, path) || '';

export const formatMessage = (template, ...args) =>
  template.replace(/{(\d+)}/g, (match, num) =>
    args[num] !== undefined ? args[num] : match
  );

export const replaceLineBreakWithBr = string => {
  if (typeof string !== 'string') {
    return null;
  }
  const splittedString = string.split('\n');
  return splittedString.length > 1
    ? splittedString.map((item, index) => (
        <span key={index}>
          {item}
          <br />
        </span>
      ))
    : string;
};

export const removeExtraSpaces = value =>
  typeof value === 'string' ? value?.replace(/ +(?= )/g, '') : value;

export const replaceLetters = value =>
  value ? value.replace(/[^0-9]/g, '') : '';

export const replaceFirstZero = value =>
  value ? value.replace(/^0(?!\.)/, '') : '';

export const replaceLeadingZeros = value =>
  typeof value === 'string' ? value.replace(/^0+(?=\d)/, '') : ''; // removes all leading zeros except one before a decimal

export const formatDecimalString = value => {
  if (!value) {
    return value;
  }

  let result = replaceLeadingZeros(value.toString())
    .replace(/^\./, '0.') // adds a zero at the start if the value begins with a dot
    .replace(/\.(?!\d)/, ''); // removes the dot if the value ends with a dot

  return result !== '0' ? result : '';
};

export const trimAllSpaces = value =>
  typeof value === 'string' ? value.replace(/\s/g, '') : value;

export const trimFirstSpace = value => {
  if (typeof value === 'string') {
    return value.replace(/^\s+/, '');
  }
  return value;
};

export const trimFirstAndLastSpaces = value => {
  if (typeof value === 'string') {
    return value.replace(/^\s+|\s+$/g, '');
  }
  return value;
};

export const restrictToDigitsAndLimit = (value, limit = 3) =>
  value ? replaceFirstZero(replaceLetters(value)).slice(0, limit) : '';

export const restrictToLimit = (value, limit) =>
  typeof value === 'string' && limit ? value.slice(0, limit) : value;

export const formatPrice = (value, currency = STRINGS.CURRENCY_SYMBOLS.GBP) => {
  if (!value) {
    return '';
  }
  return value.startsWith(currency) ? value : `${currency}${value}`;
};

export const replaceHtmlTags = value =>
  value ? value.replace(REGEX.HTML_TAGS_REGEX, '') : '';

export const trim = value => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export const asString = value => (typeof value === 'string' ? value : '');

export const trimPart = value => {
  if (value == null) {
    return '';
  }
  return String(value).trim();
};

export const joinComma = parts =>
  parts.map(trimPart).filter(Boolean).join(', ');

export const removeWhitespaces = value => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.replace(/\s/g, '');
};

export const removeWhitespacesAndSymbols = value => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.replace(/[^a-zA-Z0-9]/g, '');
};

export const truncateString = (string = '', maxVal) =>
  string?.length > maxVal ? string.substring(0, maxVal) : string;

export const capitalizeFirstLetters = str => {
  if (typeof str !== 'string') {
    return str;
  }
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const capitalizeDPD = str => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.replace(/dpd/gi, 'DPD');
};

export const formatIndividualPrice = (
  value,
  currency = STRINGS.CURRENCY_SYMBOLS.GBP
) => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return '';
  }
  return `${currency}${addTrailingZeros(n)}`;
};

export const formatPriceWithVatRate = (
  valueWithVatRate,
  valueWithoutVatRate,
  currency = STRINGS.CURRENCY_SYMBOLS.GBP
) =>
  `${formatIndividualPrice(
    valueWithVatRate,
    currency
  )} ${' '} (${formatIndividualPrice(valueWithoutVatRate, currency)} plus VAT)`;
